/**
 * The call stage: the agent matrix, large, and one line saying what is happening.
 * Compact when a transcript shares the panel with it.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "./deepagent-matrix.js";

import type { MatrixMode } from "./deepagent-matrix.js";
import type { ConversationState } from "../state/conversation-controller.js";
import type { I18nStrings } from "../i18n/index.js";

@customElement("deepagent-call-stage")
export class DeepAgentCallStage extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 28px;
      padding: 32px 24px;
      flex: 1 1 auto;
      min-height: 0;
      text-align: center;
    }

    :host([compact]) {
      flex: 0 0 auto;
      flex-direction: row;
      justify-content: flex-start;
      gap: 16px;
      padding: 14px 16px;
      text-align: left;
    }

    deepagent-matrix {
      --da-matrix-size: 136px;
      --da-matrix-gap: 3px;
    }

    :host([compact]) deepagent-matrix {
      --da-matrix-size: 64px;
      --da-matrix-gap: 2px;
    }

    .copy {
      display: flex;
      flex-direction: column;
      gap: 4px;
      max-width: 280px;
    }

    .title {
      margin: 0;
      font-family: var(--da-font-display);
      font-size: 16px;
      font-weight: 600;
      letter-spacing: -0.015em;
      color: var(--da-text);
    }

    .text {
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
      color: var(--da-text-muted);
    }
  `;

  @property({ type: String }) state: ConversationState = "idle";
  @property({ type: String }) agentMode: "speaking" | "listening" | "idle" = "idle";
  /** Reflected so the stylesheet can shrink the stage without a class map. */
  @property({ type: Boolean, reflect: true }) compact = false;
  @property({ type: Object }) strings!: I18nStrings;
  /** Who the visitor is talking to, the title of a running call's stage. */
  @property({ type: String }) agentName = "";

  /** Callbacks to pull live FFT data from the conversation controller. */
  @property({ attribute: false })
  outputFreqData: (() => Uint8Array | null) | null = null;
  @property({ attribute: false })
  inputFreqData: (() => Uint8Array | null) | null = null;

  private get copy(): { title: string; text: string | null } {
    const s = this.strings;
    switch (this.state) {
      case "requesting_mic":
        return { title: s.micPrompt, text: s.micPromptHint };
      case "connecting":
        return { title: s.connecting, text: null };
      case "active": {
        const doing =
          this.agentMode === "speaking" ? s.agentSpeaking : s.agentListening;
        // Compact sits under the header, which already names the agent.
        return this.compact
          ? { title: doing, text: null }
          : { title: this.agentName, text: doing };
      }
      case "ended":
        return { title: s.callEnded, text: null };
      default:
        return { title: s.callHint, text: s.micNotice };
    }
  }

  override render() {
    const isActive = this.state === "active";
    const mode: MatrixMode = isActive
      ? this.agentMode === "speaking"
        ? "speaking"
        : "listening"
      : this.state === "connecting" || this.state === "requesting_mic"
        ? "connecting"
        : "idle";
    // Output when the agent speaks, input when it listens, nothing off a call.
    const dataSource = isActive
      ? this.agentMode === "speaking"
        ? this.outputFreqData
        : this.inputFreqData
      : null;
    const { title, text } = this.copy;

    return html`
      <deepagent-matrix
        .cells=${9}
        .mode=${mode}
        .dataSource=${dataSource}
        ?glow=${!this.compact}
      ></deepagent-matrix>
      <div class="copy" role=${isActive ? nothing : "status"}>
        ${title ? html`<p class="title">${title}</p>` : null}
        ${text ? html`<p class="text">${text}</p>` : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-call-stage": DeepAgentCallStage;
  }
}
